import React, { useEffect, useMemo } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import {
  useGetJiraIssueTypes,
  useGetJiraIssueTypesByProject,
  useGetJiraProjects
} from '../JiraConnect.hooks';
import { JiraIssueType } from '../JiraConnect.interface';

export interface JiraIssueTypePickerProps {
  value?: string;
  disabled?: boolean;
  projectId?: string;
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onChange?: (value?: {
    id: string;
    label: string;
    issueType: JiraIssueType;
  }) => void;
}

const JiraIssueTypePicker: React.FC<JiraIssueTypePickerProps> = props => {
  const { value, disabled, projectId, textFieldProps, onChange } = props;
  const [
    { data: issueTypes, isLoading: isLoadingIssueType, error: issueTypeError },
    getIssueTypes
  ] = useGetJiraIssueTypes();
  const [
    {
      data: projectIssueTypes,
      isLoading: isLoadingProjectIssueTypes,
      error: projectIssueTypeError
    },
    getProjectIssueTypes
  ] = useGetJiraIssueTypesByProject();
  const [{ data: projects }, getProjects] = useGetJiraProjects();

  const isLoading = isLoadingIssueType || isLoadingProjectIssueTypes;
  const error = issueTypeError || projectIssueTypeError;

  const options = useMemo(() => {
    // optionally include associated issue type project in labels
    const data = projectId ? projectIssueTypes : issueTypes;
    return (data || []).map(issueType => {
      const projectId = issueType.scope?.project?.id;
      const project = projectId
        ? projects?.find(project => project.id === projectId)
        : undefined;
      return {
        value: issueType.id,
        label: issueType.name + (project ? ` (${project.key})` : ''),
        issueType
      };
    });
  }, [issueTypes, projectId, projectIssueTypes, projects]);

  const selected = useMemo(() => {
    return value ? options.find(option => option.value === value) : undefined;
  }, [options, value]);

  useEffect(() => {
    getProjects();
  }, [getProjects]);

  useEffect(() => {
    if (projectId) {
      getProjectIssueTypes({ data: { projectId } });
    } else {
      getIssueTypes();
    }
  }, [projectId, getIssueTypes, getProjectIssueTypes]);

  return (
    <Autocomplete
      className='field'
      options={options}
      disabled={disabled}
      loading={isLoading}
      value={selected || null}
      getOptionLabel={option => option.label}
      onChange={(_, value) => {
        const selected = value
          ? { id: value.value, label: value.label, issueType: value.issueType }
          : undefined;
        onChange?.(selected);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Issue Type'
          error={!!error?.message}
          helperText={error?.message}
          {...textFieldProps}
          InputProps={{ ...params.InputProps, ...textFieldProps?.InputProps }}
          InputLabelProps={{
            ...params.InputLabelProps,
            ...textFieldProps?.InputLabelProps
          }}
        />
      )}
    />
  );
};

export default JiraIssueTypePicker;
